/* eslint-disable no-restricted-imports */

import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { FormatMoney } from 'components';
import { FinancialSummaryCard, calculateFinancialTotals } from 'components/Contigo';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
  reviewCard: {
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[2],
  },
  reviewTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  boldText: {
    fontWeight: 600,
  },
  sellPrice: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  notesField: {
    marginTop: theme.spacing(3),
  },
  tableContainer: {
    overflowX: 'auto',
  },
  productCell: {
    minWidth: 180,
    [theme.breakpoints.down('sm')]: {
      minWidth: 150,
    },
  },
}));

export default function ReviewStep({ customer, orderItems, notes, onNotesChange }) {
  const classes = useStyles();
  const totals = calculateFinancialTotals(orderItems);

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Revisar Pedido
      </Typography>

      <Card className={classes.reviewCard}>
        <CardContent>
          <Typography variant='subtitle1' className={classes.reviewTitle}>
            Cliente
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                Nome/Empresa
              </Typography>
              <Typography variant='body1'>
                {customer?.NOME} {customer?.APELIDO && `(${customer.APELIDO})`}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='textSecondary'>
                CNPJ/CPF
              </Typography>
              <Typography variant='body1'>{customer?.CGC}</Typography>
            </Grid>
            {customer?.EMAIL && (
              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='textSecondary'>
                  E-mail
                </Typography>
                <Typography variant='body1'>{customer.EMAIL}</Typography>
              </Grid>
            )}
            {(customer?.DDD1 || customer?.FONE) && (
              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='textSecondary'>
                  Telefone
                </Typography>
                <Typography variant='body1'>
                  {customer?.DDD1 && customer?.FONE
                    ? `(${customer.DDD1}) ${customer.FONE}`
                    : customer?.FONE || ''}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.reviewCard}>
        <CardContent>
          <Typography variant='subtitle1' className={classes.reviewTitle}>
            Produtos ({orderItems.length} {orderItems.length === 1 ? 'item' : 'itens'})
          </Typography>
          <TableContainer className={classes.tableContainer}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.productCell}>
                    <strong>Produto</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Qtd</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>Custo Un.</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>Venda Un.</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>Total</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderItems.map((item) => (
                  <TableRow key={item.part_number} hover>
                    <TableCell className={classes.productCell}>
                      <Typography variant='body2' className={classes.boldText}>
                        {item.part_number}
                      </Typography>
                      <Typography variant='caption' color='textSecondary'>
                        {item.description}
                      </Typography>
                    </TableCell>
                    <TableCell align='center'>{item.quantity}</TableCell>
                    <TableCell align='right'>
                      <FormatMoney>{item.cost_price}</FormatMoney>
                    </TableCell>
                    <TableCell align='right'>
                      <FormatMoney>{item.sell_price}</FormatMoney>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography className={classes.sellPrice}>
                        <FormatMoney>{item.quantity * item.sell_price}</FormatMoney>
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <FinancialSummaryCard
        totalCost={totals.totalCost}
        totalSell={totals.totalSell}
        profit={totals.profit}
        margin={totals.margin}
      />

      <TextField
        fullWidth
        multiline
        minRows={4}
        label='Observações (opcional)'
        variant='outlined'
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        className={classes.notesField}
      />
    </Box>
  );
}

ReviewStep.propTypes = {
  customer: PropTypes.object,
  orderItems: PropTypes.array.isRequired,
  notes: PropTypes.string,
  onNotesChange: PropTypes.func.isRequired,
};
